import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, UserCheck, Clock, CheckCircle2, XCircle, AlertCircle, FileText, CalendarDays } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface Activity {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  status: string;
  days_of_week: number[];
}

interface Student {
  id: string;
  name: string;
  class: string;
  currentAttendance?: {
    id: string;
    status: string;
  };
}

const attendanceStatuses = [
  { value: 'present', label: 'Presente', icon: CheckCircle2, color: 'bg-green-500' },
  { value: 'absent', label: 'Ausente', icon: XCircle, color: 'bg-red-500' },
  { value: 'late', label: 'Tardanza', icon: Clock, color: 'bg-orange-500' },
  { value: 'justified', label: 'Justificado', icon: FileText, color: 'bg-blue-500' }
];

export const AttendanceCalendar = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedActivity, setSelectedActivity] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activities, setActivities] = useState<Activity[]>([]);
  const [todayActivities, setTodayActivities] = useState<Activity[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState<string>("");
  const { toast } = useToast();

  useEffect(() => {
    loadActivities();
    loadAllStudents();
  }, []);

  useEffect(() => {
    filterActivitiesForDate(selectedDate);
  }, [selectedDate, activities]);

  useEffect(() => {
    if (selectedActivity) {
      loadStudentsWithAttendance(selectedActivity);
    }
  }, [selectedActivity, selectedDate]);

  // Actualizar reloj cada segundo
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toTimeString().split(' ')[0].substring(0, 5));
    };
    
    updateTime(); // Actualizar inmediatamente
    const interval = setInterval(updateTime, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const loadActivities = async () => {
    try {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('status', 'active')
        .order('start_time');
      
      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error('Error loading activities:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las actividades",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllStudents = async () => {
    try {
      const { data, error } = await supabase
        .from('students')
        .select('id, name, class')
        .order('name');
      
      if (error) throw error;
      setAllStudents(data || []);
    } catch (error) {
      console.error('Error loading students:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los estudiantes",
        variant: "destructive"
      });
    }
  };

  const filterActivitiesForDate = (date: Date) => {
    const dayOfWeek = date.getDay(); // 0 = domingo, 1 = lunes, etc.
    const dayIndex = dayOfWeek === 0 ? 7 : dayOfWeek; // Convertir domingo a 7
    
    const activitiesForDay = activities.filter(activity => 
      activity.days_of_week.includes(dayIndex)
    );
    
    setTodayActivities(activitiesForDay);
    
    // Si la actividad seleccionada ya no está disponible en este día, limpiar selección
    if (selectedActivity && !activitiesForDay.find(a => a.id === selectedActivity)) {
      setSelectedActivity("");
    }
  };

  const loadStudentsWithAttendance = async (activityId: string) => {
    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      // Cargar asistencias existentes para esta actividad y fecha
      const { data: attendances, error } = await supabase
        .from('attendances')
        .select('student_id, status, id')
        .eq('activity_id', activityId)
        .eq('date', formattedDate);
      
      if (error) throw error;

      // Agregar información de asistencia a todos los estudiantes
      const studentsWithAttendance = allStudents.map(student => {
        const attendance = attendances?.find(att => att.student_id === student.id);
        return {
          ...student,
          currentAttendance: attendance ? {
            id: attendance.id,
            status: attendance.status
          } : undefined
        };
      });

      setAllStudents(studentsWithAttendance);
    } catch (error) {
      console.error('Error loading attendance:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar la asistencia",
        variant: "destructive"
      });
    }
  };

  const handleRegisterAttendance = async (studentId: string, status: string) => {
    if (!selectedActivity) return;

    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const student = allStudents.find(s => s.id === studentId);
      
      if (student?.currentAttendance) {
        // Actualizar asistencia existente
        const { error } = await supabase
          .from('attendances')
          .update({ status })
          .eq('id', student.currentAttendance.id);
        
        if (error) throw error;
      } else {
        // Crear nueva asistencia
        const { error } = await supabase
          .from('attendances')
          .insert({
            student_id: studentId,
            activity_id: selectedActivity,
            status,
            date: formattedDate,
            time: new Date().toTimeString().split(' ')[0]
          });
        
        if (error) throw error;
      }
      
      const activity = todayActivities.find(a => a.id === selectedActivity);
      const statusConfig = attendanceStatuses.find(s => s.value === status);
      const currentTime = new Date().toTimeString().split(' ')[0].substring(0, 5);
      const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
      const dateText = isToday ? 'hoy' : `el ${format(selectedDate, "d 'de' MMMM", { locale: es })}`;
      
      toast({
        title: "Asistencia registrada",
        description: `${student?.name} marcado como ${statusConfig?.label.toLowerCase()} en ${activity?.name} para ${dateText} a las ${currentTime}`,
      });

      // Recargar asistencias
      await loadStudentsWithAttendance(selectedActivity);
      
      // Recargar historial si está disponible
      if ((window as any).refreshAttendanceHistory) {
        (window as any).refreshAttendanceHistory();
      }
    } catch (error) {
      console.error('Error registering attendance:', error);
      toast({
        title: "Error",
        description: "No se pudo registrar la asistencia",
        variant: "destructive"
      });
    }
  };

  const filteredStudents = allStudents.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.class.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const config = attendanceStatuses.find(s => s.value === status);
    return config ? (
      <Badge className={`${config.color} text-white text-xs`}>
        {config.label}
      </Badge>
    ) : null;
  };

  return (
    <div className="space-y-6">
      {/* Día actual - Tarjeta grande */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-center gap-2 text-2xl">
            <CalendarDays className="w-6 h-6 text-primary" />
            {format(selectedDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="bg-primary/10 px-4 py-2 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <span className="text-2xl font-bold text-primary">{currentTime}</span>
                </div>
                <p className="text-xs text-muted-foreground text-center">Hora actual</p>
              </div>
            </div>
            <p className="text-lg text-muted-foreground">
              {todayActivities.length} {todayActivities.length === 1 ? 'actividad disponible' : 'actividades disponibles'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Selector de fecha */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            Seleccionar Fecha
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[280px] justify-start text-left font-normal"
                >
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {format(selectedDate, "PPP", { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  disabled={(date) => {
                    // Permitir fechas hasta hoy (no futuras)
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    return date > today;
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <p className="text-center text-sm text-muted-foreground mt-2">
            Puedes registrar asistencia para fechas pasadas o para hoy
          </p>
        </CardContent>
      </Card>

      {/* Actividades del día */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="text-xl">Actividades Disponibles</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Cargando actividades...
            </div>
          ) : todayActivities.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No hay actividades programadas para hoy</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {todayActivities.map((activity) => (
                <Button
                  key={activity.id}
                  variant={selectedActivity === activity.id ? "default" : "outline"}
                  className={`h-auto p-6 ${
                    selectedActivity === activity.id ? "bg-gradient-primary shadow-button" : ""
                  }`}
                  onClick={() => setSelectedActivity(activity.id)}
                >
                  <div className="flex flex-col items-center text-center w-full">
                    <Clock className="w-8 h-8 mb-3" />
                    <span className="font-bold text-lg mb-2">{activity.name}</span>
                    <Badge variant="secondary" className="text-sm">
                      {activity.start_time} - {activity.end_time}
                    </Badge>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registro de estudiantes */}
      {selectedActivity && (
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-xl">
              Registrar Asistencia - {todayActivities.find(a => a.id === selectedActivity)?.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Buscador */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Buscar estudiante por nombre o clase..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 text-lg py-3"
                />
              </div>

              {/* Lista de estudiantes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredStudents.map((student) => (
                  <Card
                    key={student.id}
                    className="border-2 hover:border-primary/50 transition-colors"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-bold text-lg">{student.name}</p>
                          <p className="text-sm text-muted-foreground">{student.class}</p>
                        </div>
                        {student.currentAttendance && (
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground mb-1">Estado actual:</p>
                            {getStatusBadge(student.currentAttendance.status)}
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        {attendanceStatuses.map((status) => {
                          const Icon = status.icon;
                          const isActive = student.currentAttendance?.status === status.value;
                          return (
                            <Button
                              key={status.value}
                              size="sm"
                              variant={isActive ? "default" : "outline"}
                              className={`text-xs py-2 ${isActive ? status.color + " text-white" : ""}`}
                              onClick={() => handleRegisterAttendance(student.id, status.value)}
                            >
                              <Icon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {filteredStudents.length === 0 && searchTerm && (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No se encontraron estudiantes</p>
                  <p className="text-sm">Intenta con un término de búsqueda diferente</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};